import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Users, Mic, Clock, Calendar, ArrowRight, Trash2, Pencil, RefreshCw,
  Video, FileText, CheckCircle2, Loader2, AlertCircle, Presentation, QrCode, Copy, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  waiting: 'bg-amber-100 text-amber-700',
  active: 'bg-red-100 text-red-700',
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  error: 'bg-red-100 text-red-700',
};

const statusLabels = {
  waiting: 'Waiting',
  active: 'Recording',
  processing: 'Generating Minutes...',
  completed: 'Completed',
  error: 'Error',
};

// ─── Meeting Card ───
const MeetingCard = ({ meeting, onClick, onDelete, onRename, onRetry }) => {
  const dur = meeting.duration_seconds;
  const durStr = dur ? `${Math.floor(dur / 60)}m ${dur % 60}s` : '—';
  const showRetry = meeting.status === 'error' || meeting.transcript_status === 'failed' || meeting.transcript_error;

  return (
    <div className="w-full text-left p-4 rounded-xl border border-slate-700/50 bg-slate-800/50 hover:bg-slate-700/50 transition-all group relative" data-testid={`meeting-${meeting.meeting_id}`}>
      <button onClick={onClick} className="w-full text-left">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-white truncate group-hover:text-emerald-400 transition-colors">{meeting.title}</p>
              {meeting.mode === 'presentation' && <Badge className="text-[8px] bg-emerald-500/20 text-emerald-400 shrink-0">Sales</Badge>}
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {new Date(meeting.created_at).toLocaleDateString()} · {durStr} · {meeting.participants?.length || 0} participants
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">Host: {meeting.host_name}</p>
          </div>
          <Badge className={cn("text-[10px] shrink-0 ml-2", statusColors[meeting.status])}>{statusLabels[meeting.status]}</Badge>
        </div>
        {meeting.agenda?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {meeting.agenda.slice(0, 3).map((a, i) => (
              <span key={i} className="text-[9px] bg-slate-700/80 text-slate-300 px-1.5 py-0.5 rounded">{a}</span>
            ))}
            {meeting.agenda.length > 3 && <span className="text-[9px] text-slate-500">+{meeting.agenda.length - 3}</span>}
          </div>
        )}
      </button>

      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {showRetry && onRetry && (
          <button onClick={(e) => { e.stopPropagation(); onRetry(meeting.meeting_id); }}
            className="w-6 h-6 rounded-md bg-amber-500/20 hover:bg-amber-500/40 flex items-center justify-center"
            title="Retry transcription" data-testid={`retry-meeting-${meeting.meeting_id}`}>
            <RefreshCw className="h-3 w-3 text-amber-300" />
          </button>
        )}
        {onRename && (
          <button onClick={(e) => { e.stopPropagation(); onRename(meeting); }}
            className="w-6 h-6 rounded-md bg-slate-500/20 hover:bg-slate-500/40 flex items-center justify-center"
            title="Rename meeting" data-testid={`rename-meeting-${meeting.meeting_id}`}>
            <Pencil className="h-3 w-3 text-slate-300" />
          </button>
        )}
        {onDelete && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(meeting.meeting_id); }}
            className="w-6 h-6 rounded-md bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center"
            title="Delete meeting" data-testid={`delete-meeting-${meeting.meeting_id}`}>
            <Trash2 className="h-3 w-3 text-red-400" />
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ───
export default function MeetingPage() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);

  // Create meeting form
  const [title, setTitle] = useState('');
  const [agendaItems, setAgendaItems] = useState(['']);
  const [creating, setCreating] = useState(false);
  const [mode, setMode] = useState('internal'); // 'internal' or 'presentation'
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [presentationLink, setPresentationLink] = useState(null);

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(`${API}/api/meetings/list/all`);
      setMeetings(res.data.meetings || []);
    } catch { /* */ }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!title.trim()) { toast.error('Meeting title required'); return; }
    const agenda = agendaItems.filter(a => a.trim());
    if (agenda.length === 0) { toast.error('At least one agenda item required'); return; }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/meetings/create`, {
        title, agenda, mode, client_name: clientName, client_email: clientEmail
      });

      if (mode === 'presentation' && res.data.public_url) {
        // Show the shareable link instead of navigating immediately
        setPresentationLink({
          url: res.data.public_url,
          token: res.data.public_token,
          code: res.data.code,
          meetingId: res.data.meeting_id,
        });
        toast.success('Sales meeting created!');
      } else {
        toast.success(`Meeting created! Code: ${res.data.code}`);
        setCreateOpen(false);
        setTitle(''); setAgendaItems(['']); setMode('internal');
        navigate(`/meeting/${res.data.meeting_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create meeting');
    }
    setCreating(false);
  };

  const handleJoin = async () => {
    if (joinCode.length !== 6) { toast.error('Enter a 6-character meeting code'); return; }
    setJoining(true);
    try {
      const res = await axios.post(`${API}/api/meetings/join`, { code: joinCode.toUpperCase() });
      toast.success(`Joined: ${res.data.title}`);
      navigate(`/meeting/${res.data.meeting_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Meeting not found');
    }
    setJoining(false);
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm('Delete this meeting? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/meetings/${meetingId}`);
      toast.success('Meeting deleted');
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRename = async (meeting) => {
    const next = window.prompt('New meeting title', meeting.title || '');
    if (next === null) return;
    const title = next.trim();
    if (!title) { toast.error('Title cannot be empty'); return; }
    try {
      await axios.patch(`${API}/api/meetings/${meeting.meeting_id}`, { title });
      toast.success('Renamed');
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const handleRetryTranscription = async (meetingId) => {
    try {
      await axios.post(`${API}/api/meetings/${meetingId}/retry-transcription`);
      toast.success('Transcription retry queued');
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    }
  };

  const addAgendaItem = () => setAgendaItems([...agendaItems, '']);
  const removeAgendaItem = (i) => setAgendaItems(agendaItems.filter((_, idx) => idx !== i));
  const updateAgendaItem = (i, v) => { const a = [...agendaItems]; a[i] = v; setAgendaItems(a); };

  const activeMeetings = meetings.filter(m => m.status === 'waiting' || m.status === 'active');
  const pastMeetings = meetings.filter(m => m.status !== 'waiting' && m.status !== 'active');

  return (
    <div className="min-h-[80vh] -m-4 md:-m-6 lg:-m-8 p-4 md:p-6 lg:p-8 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800" data-testid="meeting-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Video className="h-6 w-6 text-emerald-400" />KonnectMeet<PageInfoTooltip pageKey="konnect-meet" />
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">AI-powered meeting recorder & minutes generator</p>
          </div>
        </div>

        {/* Create / Join Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {/* Create Meeting */}
          <button onClick={() => setCreateOpen(true)}
            className="p-6 rounded-2xl border-2 border-dashed border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 hover:border-emerald-500/50 transition-all text-left group"
            data-testid="create-meeting-btn">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Plus className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="text-base font-semibold text-white">Create Meeting</p>
            <p className="text-xs text-slate-400 mt-1">Host a new meeting and invite your team</p>
          </button>

          {/* Join Meeting */}
          <div className="p-6 rounded-2xl border border-slate-700/50 bg-slate-800/50" data-testid="join-meeting-section">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-3">
              <Users className="h-6 w-6 text-blue-400" />
            </div>
            <p className="text-base font-semibold text-white mb-3">Join Meeting</p>
            <div className="flex gap-2">
              <Input
                placeholder="Enter 6-digit code"
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 text-center text-lg tracking-[0.3em] font-mono uppercase"
                maxLength={6}
                data-testid="join-code-input"
              />
              <Button onClick={handleJoin} disabled={joining || joinCode.length !== 6}
                className="bg-blue-600 hover:bg-blue-500 shrink-0" data-testid="join-meeting-btn">
                {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Active Meetings */}
        {activeMeetings.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />Active Meetings
            </h2>
            <div className="space-y-2">
              {activeMeetings.map(m => (
                <MeetingCard key={m.meeting_id} meeting={m} onClick={() => navigate(`/meeting/${m.meeting_id}`)} onDelete={handleDelete} onRename={handleRename} onRetry={handleRetryTranscription} />
              ))}
            </div>
          </div>
        )}

        {/* Past Meetings */}
        <div>
          <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" />Meeting Archive
          </h2>
          {loading ? (
            <div className="text-center py-8"><Loader2 className="h-5 w-5 text-slate-500 animate-spin mx-auto" /></div>
          ) : pastMeetings.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">No meetings yet. Create your first meeting above.</div>
          ) : (
            <div className="space-y-2">
              {pastMeetings.map(m => (
                <MeetingCard key={m.meeting_id} meeting={m} onClick={() => {
                  if (m.status === 'completed') navigate(`/meeting/${m.meeting_id}/minutes`);
                  else navigate(`/meeting/${m.meeting_id}`);
                }} onDelete={handleDelete} onRename={handleRename} onRetry={handleRetryTranscription} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Meeting Dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) { setPresentationLink(null); setMode('internal'); } }}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle className="text-white">Create New Meeting</DialogTitle></DialogHeader>

          {presentationLink ? (
            /* ─── Presentation Link & QR Screen ─── */
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-2">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                </div>
                <p className="text-sm font-semibold text-white">Meeting Created!</p>
                <p className="text-xs text-slate-400 mt-1">Share this link or QR code with your client</p>
              </div>

              <div className="flex justify-center p-4 bg-white rounded-xl" data-testid="presentation-qr">
                <QRCodeSVG value={presentationLink.url} size={180} level="M" />
              </div>

              <div className="flex items-center gap-2 p-2.5 bg-slate-900 rounded-lg border border-slate-700">
                <input className="flex-1 text-xs text-slate-300 bg-transparent outline-none" value={presentationLink.url} readOnly data-testid="presentation-url" />
                <Button size="sm" variant="ghost" className="shrink-0 h-7 text-emerald-400 hover:text-emerald-300" data-testid="copy-presentation-url"
                  onClick={() => { navigator.clipboard.writeText(presentationLink.url); toast.success('Link copied!'); }}>
                  <Copy className="h-3.5 w-3.5 mr-1" />Copy
                </Button>
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-400 justify-center">
                <span>Meeting Code: <strong className="text-white font-mono">{presentationLink.code}</strong></span>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-slate-600 text-slate-300" data-testid="open-in-new-tab"
                  onClick={() => window.open(presentationLink.url, '_blank')}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Send to Client
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="go-to-meeting"
                  onClick={() => { setCreateOpen(false); setPresentationLink(null); setMode('internal'); navigate(`/meeting/${presentationLink.meetingId}`); }}>
                  <Video className="h-3.5 w-3.5 mr-1.5" />Enter Meeting
                </Button>
              </div>
            </div>
          ) : (
            /* ─── Creation Form ─── */
            <div className="space-y-4">
              {/* Mode Toggle */}
              <div className="flex gap-2 p-1 bg-slate-900/50 rounded-lg" data-testid="meeting-mode-toggle">
                <button onClick={() => setMode('internal')}
                  className={cn("flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-medium transition-all",
                    mode === 'internal' ? "bg-slate-700 text-white" : "text-slate-400 hover:text-slate-300")}
                  data-testid="mode-internal">
                  <Users className="h-3.5 w-3.5" />Team Meeting
                </button>
                <button onClick={() => setMode('presentation')}
                  className={cn("flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-medium transition-all",
                    mode === 'presentation' ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-slate-300")}
                  data-testid="mode-presentation">
                  <Presentation className="h-3.5 w-3.5" />Sales Meeting
                </button>
              </div>

              {mode === 'presentation' && (
                <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-[10px] text-emerald-400">
                    Creates a shareable link + QR code for your client to join via their phone/browser. No login needed.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-300">Meeting Title</Label>
                <Input placeholder="e.g. Product Demo for ACME Corp" value={title} onChange={e => setTitle(e.target.value)}
                  className="mt-1 bg-slate-700/50 border-slate-600 text-white" data-testid="meeting-title-input" />
              </div>

              {mode === 'presentation' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-300">Client Name</Label>
                    <Input placeholder="Client name" value={clientName} onChange={e => setClientName(e.target.value)}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white text-sm" data-testid="client-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-300">Client Email</Label>
                    <Input placeholder="client@email.com" value={clientEmail} onChange={e => setClientEmail(e.target.value)}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white text-sm" data-testid="client-email-input" type="email" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-300">Agenda Items</Label>
                <div className="space-y-2 mt-1">
                  {agendaItems.map((item, i) => (
                    <div key={i} className="flex gap-1.5">
                      <Input placeholder={`Agenda item ${i + 1}`} value={item} onChange={e => updateAgendaItem(i, e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white text-sm" data-testid={`agenda-item-${i}`} />
                      {agendaItems.length > 1 && (
                        <Button variant="ghost" size="icon" className="shrink-0 h-9 w-9 text-slate-500 hover:text-red-400" onClick={() => removeAgendaItem(i)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" className="text-emerald-400 text-xs gap-1 hover:text-emerald-300" onClick={addAgendaItem} data-testid="add-agenda-btn">
                    <Plus className="h-3 w-3" />Add Agenda Item
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-400">Cancel</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-500" data-testid="confirm-create-meeting">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Video className="h-4 w-4 mr-2" />}
                  {mode === 'presentation' ? 'Create & Get Link' : 'Create Meeting'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
